import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import Button from '../../../components/Button';
import { MenuItem } from '../../../components/Menu';
import embedIcon from '../../../icons/editor/embed.svg';
import newTabIcon from '../../../icons/editor/newTab.svg';
import EDITOR_INTEGRATION_MENTIONS from '../../mentions/EditorIntegrationMentions';
import { useQuill } from '../../QuillContext';
import type { Blot, EmbedBlot } from '../../types';
import getEditorMentionUrl from '../../utils/getEditorMentionUrl';
import matchMention from '../../utils/matchMention';
import replaceBlotWith from '../../utils/replaceBlotWith';
import DisplayAsLinkMenuItem from '../DisplayAsLinkMenuItem';
import { checkmarkIcon, editLinkIcon } from '../editorIcons';
import ToolbarButton, { ToolbarButtonGroup } from './buttons/ToolbarButton';
import ToolbarCommentButtonGroup from './buttons/ToolbarCommentButtonGroup';
import ToolbarOverflowMenuButton from './buttons/ToolbarOverflowMenuButton';
import styles from './MentionToolbar.module.scss';

interface MentionToolbarEditorProps {
  blot: EmbedBlot;
  initialLink: string;
  initialText: string;
  textPlaceholder: string;
  editingTarget: 'link' | 'text';
  onEditingTargetChange: (target: 'link' | 'text' | null) => void;
  onSave: (value: string) => void;
}

const unsavedLink = new WeakMap<Blot, string>();
const unsavedText = new WeakMap<Blot, string>();

const MentionToolbarEditor = ({
  blot,
  initialLink,
  initialText,
  textPlaceholder,
  editingTarget,
  onEditingTargetChange,
  onSave,
}: MentionToolbarEditorProps) => {
  const [link, setLink] = useState(unsavedLink.get(blot) || initialLink);
  const [text, setText] = useState(unsavedText.get(blot) || initialText);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSave = () => {
    if (editingTarget === 'text') {
      onSave(text);
      unsavedText.delete(blot);
    } else {
      onSave(link);
      unsavedLink.delete(blot);
    }
    onEditingTargetChange(null);
  };

  return (
    <div className={styles.editor}>
      <div className={styles.tab}>
        <Button
          variant="muted"
          className={classNames(styles.tabItem, {
            [styles.active]: editingTarget === 'link',
          })}
          onClick={() => {
            onEditingTargetChange('link');
            inputRef.current?.focus();
          }}
        >
          Link
        </Button>
        <Button
          variant="muted"
          className={classNames(styles.tabItem, {
            [styles.active]: editingTarget === 'text',
          })}
          onClick={() => {
            onEditingTargetChange('text');
            inputRef.current?.focus();
          }}
        >
          Text
        </Button>
      </div>
      <div className={styles.inputContainer}>
        <input
          ref={inputRef}
          autoFocus
          // Don't use <form> to allow nested inside a form (ex TopicEditModal)
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleSave();
              event.preventDefault();
            } else if (event.key === 'Escape') {
              onEditingTargetChange(null);
            }
          }}
          value={editingTarget === 'text' ? text : link}
          placeholder={editingTarget === 'text' ? textPlaceholder : ''}
          onChange={(e) => {
            const { value } = e.target;
            if (editingTarget === 'text') {
              unsavedText.set(blot, value);
              setText(value);
            } else {
              unsavedLink.set(blot, value);
              setLink(e.target.value);
            }
          }}
        />
        <Button
          className={styles.saveButton}
          variant="link"
          disabled={
            editingTarget === 'link'
              ? !link || link === initialLink
              : text === initialText
          }
          icon={checkmarkIcon}
          onClick={() => handleSave()}
        />
      </div>
    </div>
  );
};

interface MentionToolbarProps {
  blot: EmbedBlot;
  initialEditingTarget?: 'link' | 'text';
}

const MentionToolbar = ({
  blot,
  initialEditingTarget,
}: MentionToolbarProps) => {
  const { quill, readOnly } = useQuill();
  const [link, setLink] = useState<string | null>(null);
  const [initialText, setInitialText] = useState<string | null>(null);
  const [textPlaceholder, setTextPlaceholder] = useState<string | null>(null);
  const [editingTarget, setEditingTarget] = useState<null | 'link' | 'text'>(
    initialEditingTarget || null,
  );
  const [canBeRenderedAsEmbed, setCanBeRenderedAsEmbed] = useState(false);

  useEffect(() => {
    const { id, type } = blot.statics.value(blot.domNode);
    const formats = blot.statics.formats(blot.domNode) || {};
    setLink(getEditorMentionUrl({ id, type, content: formats.content }));

    if (
      type === 'post' ||
      EDITOR_INTEGRATION_MENTIONS[type]?.modes.includes('embed')
    ) {
      setCanBeRenderedAsEmbed(true);
    }
  }, [blot]);

  if (editingTarget) {
    return (
      <MentionToolbarEditor
        blot={blot}
        initialLink={link || ''}
        initialText={initialText || ''}
        textPlaceholder={textPlaceholder || ''}
        editingTarget={editingTarget}
        onEditingTargetChange={setEditingTarget}
        onSave={(value) => {
          if (!quill) return;

          if (editingTarget === 'link') {
            const mention = matchMention(value);
            if (mention) {
              replaceBlotWith(quill, blot, { mention: mention.value });
            } else {
              replaceBlotWith(quill, blot, value, { link: value });
            }
          } else if (editingTarget === 'text') {
            if (
              !value ||
              value === blot.statics.formats(blot.domNode)?.content
            ) {
              blot.format('mention-text', null);
            } else {
              blot.format('mention-text', value);
            }
          }
        }}
      />
    );
  }

  return (
    <>
      {!readOnly && (
        <ToolbarButtonGroup>
          <ToolbarButton
            icon={editLinkIcon}
            tooltip="Edit"
            onClick={() => {
              const formats = blot.statics.formats(blot.domNode) || {};
              setInitialText(formats['mention-text'] || formats.content);
              setTextPlaceholder(formats.content);
              setEditingTarget('link');
            }}
            testId="mentionToolbarEditButton"
          />
        </ToolbarButtonGroup>
      )}
      <ToolbarCommentButtonGroup blot={blot} />
      {link && (
        <ToolbarButtonGroup>
          <ToolbarOverflowMenuButton testId="mentionToolbarMenuButton">
            {(onClose) => (
              <>
                <MenuItem
                  icon={newTabIcon}
                  to={link}
                  target="_blank"
                  onClick={onClose}
                >
                  Open in new tab
                </MenuItem>
                {quill && canBeRenderedAsEmbed && (
                  <MenuItem
                    icon={embedIcon}
                    onClick={() => {
                      const { id, type } = blot.statics.value(blot.domNode);
                      replaceBlotWith(quill, blot, { embed: { type, id } });
                    }}
                    testId="displayAsEmbedMenuItem"
                  >
                    Display as embed
                  </MenuItem>
                )}
                {!readOnly && quill && (
                  <DisplayAsLinkMenuItem
                    quill={quill}
                    blot={blot}
                    link={link}
                  />
                )}
              </>
            )}
          </ToolbarOverflowMenuButton>
        </ToolbarButtonGroup>
      )}
    </>
  );
};

export default MentionToolbar;
